import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reload
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, functions } from './config';

// Branded auth emails (verification / password reset / email change) are sent by
// the sendAuthEmail Cloud Function through Resend — Firebase's built-in templates
// are locked and plain. See functions/authEmails.js.
const sendAuthEmailFn = httpsCallable(functions, 'sendAuthEmail');

// Auth providers
const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();

// Create user profile in Firestore
const createUserProfile = async (user, additionalData = {}) => {
  if (!user) return;

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    const { displayName, email, photoURL } = user;
    const createdAt = serverTimestamp();

    try {
      await setDoc(userRef, {
        displayName: displayName || additionalData.displayName || 'Tournament Organizer',
        email,
        photoURL: photoURL || null,
        createdAt,
        lastLoginAt: serverTimestamp(),
        ...additionalData
      });
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  } else {
    // Update last login time
    try {
      await setDoc(userRef, {
        lastLoginAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  return userRef;
};

// Sign up with email and password
export const signUpWithEmailAndPassword = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Update the user's display name
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }

    // Send the branded verification email (Resend Cloud Function). Non-fatal: the
    // account exists regardless, and the user can resend from the verify screen.
    try {
      await sendAuthEmailFn({ type: 'verify' });
    } catch (emailErr) {
      console.warn('Verification email send failed (account still created):', emailErr?.message);
    }

    // Create user profile in Firestore
    await createUserProfile(result.user, { displayName });

    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Check if email is verified
    if (!result.user.emailVerified) {
      // Sign out the user since they're not verified
      await signOut(auth);
      return {
        user: null,
        error: {
          code: 'auth/email-not-verified',
          message: 'Please verify your email address before signing in. Check your inbox for a verification email.'
        },
        needsVerification: true,
        userEmail: result.user.email
      };
    }

    // Update user profile (last login)
    await createUserProfile(result.user);

    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);

    // Create/update user profile in Firestore
    await createUserProfile(result.user);

    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

// Sign in with Facebook
export const signInWithFacebook = async () => {
  try {
    const result = await signInWithPopup(auth, facebookProvider);

    // Create/update user profile in Firestore
    await createUserProfile(result.user);

    return { user: result.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Reset password (branded email via Resend Cloud Function).
export const resetPassword = async (email) => {
  try {
    await sendAuthEmailFn({ type: 'reset', email });
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Request an email-address change. Reauthenticates with the current password (a
// sensitive change), then a branded confirmation link is emailed to the NEW
// address — the change only takes effect once the user clicks it. Password
// (email/login) accounts only; Google accounts get their email from Google.
export const requestEmailChange = async (currentPassword, newEmail) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');
    if (!user.email) throw new Error('This account type cannot change its email here');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    // Force a fresh ID token so the Cloud Function sees a current auth_time
    // (it rejects stale-token change requests as a takeover safeguard).
    await user.getIdToken(true);
    await sendAuthEmailFn({ type: 'changeEmail', newEmail: String(newEmail || '').trim().toLowerCase() });
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Update user profile
export const updateUserProfile = async (updates) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');

    // Update Firebase Auth profile - only pass defined values
    if (updates.displayName !== undefined || updates.photoURL !== undefined) {
      const authUpdates = {};
      if (updates.displayName !== undefined) {
        authUpdates.displayName = updates.displayName;
      }
      if (updates.photoURL !== undefined) {
        authUpdates.photoURL = updates.photoURL;
      }
      await updateProfile(user, authUpdates);
    }

    // Update Firestore profile
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Change password
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');

    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);

    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Get user profile from Firestore
export const getUserProfile = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { profile: userDoc.data(), error: null };
    } else {
      return { profile: null, error: new Error('User profile not found') };
    }
  } catch (error) {
    return { profile: null, error };
  }
};

// Send email verification to current user
// Note: For unverified users who need to resend verification, they should use
// the resendVerificationEmail function after signing in
export const sendVerificationEmail = async (userEmail) => {
  try {
    const user = auth.currentUser;
    if (user && user.email === userEmail) {
      await sendAuthEmailFn({ type: 'verify' });
      return { error: null };
    }
    // Cannot send verification email to a user who isn't signed in
    // The user needs to sign in first, then they can request verification
    return { error: new Error('Please sign in to resend verification email') };
  } catch (error) {
    return { error };
  }
};

// Resend verification email (safer approach)
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }

    await sendAuthEmailFn({ type: 'verify' });

    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Check if current user's email is verified
export const checkEmailVerification = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { isVerified: false, error: new Error('No user signed in') };
    }

    // Reload user to get fresh verification status
    await reload(user);

    return { isVerified: user.emailVerified, error: null };
  } catch (error) {
    return { isVerified: false, error };
  }
};